import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const faqs: LearnFAQ[] = [
  {
    question: "Is MeRT FDA-approved for depression?",
    answer:
      "MeRT is a protocol, not a device. The TMS devices used in MeRT (such as the NeuroStar TMS Therapy System) are FDA-cleared for major depressive disorder. The MeRT protocol itself — using EEG to personalize the treatment frequency and coil placement — is not a separately FDA-cleared protocol. SAINT® is FDA-cleared as a system (K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication.",
  },
  {
    question: "Which works faster, SAINT® or MeRT?",
    answer:
      "SAINT® is faster in calendar time: about 50 sessions over 5 consecutive days. MeRT is typically delivered in a standard TMS schedule of one session per day over 4 to 6 weeks, though some MeRT programs use an accelerated schedule. Speed is one factor — evidence, targeting, and FDA status matter too. A free consultation is the best way to compare options for your situation.",
  },
  {
    question: "Can I do both SAINT® and MeRT?",
    answer:
      "It depends on your clinical situation. If one TMS protocol did not provide enough relief, another may still be an option, but eligibility is clinical and depends on your diagnosis, medication history, and contraindications. Doing two TMS protocols back-to-back is uncommon and should be discussed with a psychiatrist. A free consultation with our clinical team is the best way to explore what fits your history.",
  },
  {
    question: "What is the difference between MeRT and standard TMS?",
    answer:
      "Standard TMS uses a fixed protocol — typically 10 Hz stimulation over the left dorsolateral prefrontal cortex for about 19 minutes per session. MeRT adds an EEG (electroencephalogram) recording to personalize the stimulation frequency and coil placement for each person. SAINT® goes further: it uses a functional MRI (fMRI) scan to personalize the exact treatment target on the prefrontal cortex, then delivers an accelerated schedule of 10 sessions per day over 5 consecutive days.",
  },
  {
    question: "Does insurance cover MeRT?",
    answer:
      "Standard TMS is the most often covered by insurance for depression. MeRT may be covered if the underlying TMS procedure is covered by your plan, but the EEG-guided personalization may not be separately reimbursed. SAINT® may also be covered; coverage varies by plan. Neuro Wellness verifies your benefits for free, whatever carrier you have, so you know what your plan allows before you commit.",
  },
];

export const pageSaintVsMert = makeArticle({
  slug: "saint-vs-mert",
  title: "SAINT® vs MeRT for Depression",
  description:
    "How SAINT® compares to MeRT on evidence, FDA status, time, cost and who it's for.",
  canonical: "/learn/saint-vs-mert",
  h1: "SAINT® vs MeRT for Treatment-Resistant Depression",
  breadcrumbLabel: "SAINT® vs MeRT",
  shortAnswer:
    "SAINT® and MeRT (Magnetic e-Resonance Therapy) are both approaches to transcranial magnetic stimulation (TMS) for depression, but they differ in how they personalize treatment, what evidence supports them, and how long the course takes. SAINT® is an FDA-cleared system that uses a functional MRI (fMRI) scan to personalize the treatment target and delivers 10 sessions per day over 5 consecutive days. MeRT uses an EEG (electroencephalogram) to guide TMS frequency and placement, typically over a standard 4-to-6-week schedule. The right choice depends on your diagnosis, schedule, and how much evidence you want behind the specific protocol.",
  keyTakeaways: [
    "SAINT® is FDA-cleared (K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication; it uses fMRI-guided personalized targeting over 5 consecutive days.",
    "MeRT is a protocol that uses EEG to personalize TMS frequency and coil placement; the TMS devices used in MeRT are FDA-cleared for depression, but the MeRT protocol itself is not a separately FDA-cleared protocol.",
    "SAINT® at Neuro Wellness starts at $25,000; standard TMS (including MeRT-style protocols) may be covered — coverage varies by plan, and we verify your benefits for free.",
    "Neither protocol is a cure or guaranteed; individual results may vary. A free consultation is the best way to learn which fits your situation.",
  ],
  sections: [
    {
      heading:
        "Quick answer: SAINT® vs MeRT for treatment-resistant depression",
      paragraphs: [
        "If you are comparing SAINT® and MeRT, you are comparing two approaches to transcranial magnetic stimulation (TMS) — a non-invasive treatment that uses magnetic pulses to stimulate areas of the brain involved in mood regulation. Both aim to personalize TMS, but they do it differently and have different evidence bases.",
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered on the Magnus Medical SAINT Neuromodulation System. It uses a functional MRI (fMRI) scan to identify each person's individual treatment target, then delivers 10 sessions per day over 5 consecutive days — about 50 sessions total. It is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
        "MeRT (Magnetic e-Resonance Therapy) is a protocol that uses an EEG (electroencephalogram) — a recording of the brain's electrical activity — to personalize the TMS stimulation frequency and coil placement. MeRT is typically delivered on a standard TMS schedule of one session per day over 4 to 6 weeks. The TMS devices used in MeRT are FDA-cleared for depression, but the MeRT protocol itself is not a separately FDA-cleared protocol.",
        "Both are legitimate approaches to brain-based depression treatment. The question is which fits your diagnosis, your schedule, and how much evidence you want behind the specific protocol. Learn more about [[SAINT®|/saint]] and take the [[free candidacy screener|/am-i-a-candidate-screener]] to see which may apply to you.",
      ],
    },
    {
      heading: "What MeRT is and its FDA/regulatory status",
      paragraphs: [
        "MeRT stands for Magnetic e-Resonance Therapy. It is a clinical protocol developed by the Brain Treatment Institute (BTI) that combines TMS with quantitative EEG (qEEG) analysis. The EEG recording is used to identify each person's brainwave patterns, and the clinician uses that information to set the stimulation frequency and coil placement for TMS sessions.",
        "MeRT is a protocol, not a device. The TMS devices used in MeRT — such as the NeuroStar TMS Therapy System — are FDA-cleared for major depressive disorder. However, the MeRT protocol itself, which adds EEG-guided personalization on top of standard TMS, is not a separately FDA-cleared protocol. This means the FDA clearance covers the device and its cleared indications, not the specific MeRT methodology.",
        "This distinction matters. A clinic offering MeRT is using an FDA-cleared TMS device, but the EEG-guided personalization layer is a clinical protocol that has not received separate FDA clearance as a distinct treatment system. SAINT®, by contrast, is FDA-cleared as a complete system — the Magnus Medical SAINT Neuromodulation System received 510(k) clearance (K220177) and a Breakthrough Device designation specifically for the SAINT® protocol, including the fMRI-guided targeting software.",
        "If you are considering MeRT, ask the clinic which TMS device it uses, what that device is FDA-cleared for, and what published outcome data supports the MeRT protocol specifically. For more on what treatment-resistant depression means and how TMS fits, see our [[treatment-resistant depression guide|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "Evidence side by side: SAINT® vs MeRT",
      paragraphs: [
        "The evidence base for SAINT® and MeRT differs in design and specificity. SAINT® has published randomized controlled trial (RCT) data for the SAINT® protocol on the Magnus system. MeRT has published studies, but the evidence base is smaller and less specific to a single standardized protocol.",
        "In the 2022 randomized trial, 79% of participants reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. That figure comes from the SAINT® trial and applies to the SAINT® protocol on the Magnus system — it does not transfer to MeRT, standard TMS, or any other TMS protocol.",
        "MeRT studies have reported improvements in depression scores, but the published evidence is less extensive than SAINT®'s RCT data and is based on smaller, often open-label studies. Ask any clinic offering MeRT for its own published outcome data for the specific protocol and device it uses.",
        "For a deeper look at SAINT® remission and response data, see our [[SAINT® TMS success rates page|/learn/saint-tms-success-rates]].",
      ],
      table: {
        headers: ["", "SAINT® (Magnus)", "MeRT (EEG-guided TMS)"],
        rows: [
          [
            "FDA status",
            "FDA-cleared system (K220177) for MDD in adults with inadequate response to prior antidepressants; Breakthrough Device designation",
            "TMS device FDA-cleared for MDD; MeRT protocol not separately FDA-cleared",
          ],
          [
            "Invasiveness",
            "Non-invasive; no anesthesia or sedation; outpatient",
            "Non-invasive; no anesthesia or sedation; outpatient",
          ],
          [
            "Time to complete a course",
            "5 consecutive days; 10 sessions/day; ~50 sessions total",
            "Typically 4–6 weeks; 1 session/day; ~36 sessions",
          ],
          [
            "Personalization method",
            "fMRI-guided targeting of the DLPFC per individual brain scan",
            "EEG/qEEG-guided selection of stimulation frequency and coil placement",
          ],
          [
            "Published evidence",
            "Randomized controlled trial (Cole et al., 2022); 79% remission — individual results may vary",
            "Published studies, smaller and less standardized than SAINT® RCT data",
          ],
          [
            "Cost at NWCOA",
            "Starts at $25,000; may be covered, varies by plan; financing available",
            "Not offered at NWCOA; standard TMS may be covered, varies by plan",
          ],
        ],
      },
    },
    {
      heading: "Time, cost, and invasiveness: SAINT® vs MeRT",
      paragraphs: [
        "Both SAINT® and MeRT are non-invasive, outpatient treatments. Neither requires anesthesia, sedation, or a hospital stay. The patient sits in a chair while a coil placed on the scalp delivers magnetic pulses. The main differences are time and cost.",
        "SAINT® compresses the entire course into 5 consecutive days, with 10 sessions per day. You are in the clinic for most of the day during treatment week, but the course is over in one week instead of six. MeRT, like standard TMS, typically runs one session per day over 4 to 6 weeks — about 36 sessions.",
        "On cost: SAINT® at Neuro Wellness starts at $25,000. That price reflects the fMRI scan, the FDA-cleared targeting software, the Magnus system, physician oversight, and the full 50-session week. Standard TMS (including MeRT-style protocols) may be covered by insurance; coverage varies by plan. SAINT® may also be covered — coverage varies by plan. We verify your benefits for free, whatever carrier you have, so you know what your plan allows before you commit. Financing is available for SAINT® on a case-by-case basis and is discussed during your consultation.",
        "For the full SAINT® cost breakdown, see our [[SAINT® TMS cost page|/learn/saint-tms-cost]]. To check what your plan covers, start with a [[free benefits verification|/check-insurance-options]].",
      ],
    },
    {
      heading: "Who each protocol fits",
      paragraphs: [
        "SAINT® fits adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication and who can commit to 5 consecutive days in the clinic. The compressed schedule suits people who want to avoid six weeks of daily visits and who want a protocol with fMRI-guided personalized targeting and published randomized trial data. It is not for people with non-removable magnetic-sensitive metal near the head, certain implanted devices, or increased intracranial pressure.",
        "MeRT may appeal to adults who want EEG-guided personalization of their TMS treatment and who can commit to a standard 4-to-6-week schedule. Because the MeRT protocol is not separately FDA-cleared, it is best suited for someone who has reviewed the evidence and is comfortable with a protocol that has a smaller published evidence base than SAINT® or standard TMS.",
        "Standard TMS remains the option with the largest evidence base and the most established insurance coverage. Its trade-off is time: 4 to 6 weeks of daily visits. Neuro Wellness offers SAINT® and standard TMS, along with SPRAVATO® (esketamine), IV ketamine, psychotherapy, and medication management. A [[free consultation|/contactus]] is the best way to learn which fits your diagnosis and circumstances.",
        "For more comparisons, see our guides to [[SAINT® vs accelerated TMS|/learn/saint-vs-accelerated-tms]], [[SAINT® vs deep TMS|/learn/saint-vs-deep-tms]], and [[what accelerated TMS is|/what-is-accelerated-tms]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.21060580",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression: 1-year follow-up. Am J Psychiatry. 2020;177(10):970–978.",
      url: "https://doi.org/10.1176/appi.ajp.2020.20040470",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification K220177 — Magnus Medical SAINT Neuromodulation System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — NeuroStar TMS Therapy System (and other TMS devices cleared for major depressive disorder).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Neuromodulation System — Breakthrough Device designation and FDA clearance.",
      url: "https://www.magnusmedical.com",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
      description:
        "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System: 10 sessions per day for 5 consecutive days.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Magnetic e-Resonance Therapy (MeRT)",
      description:
        "EEG-guided TMS protocol that personalizes stimulation frequency and coil placement; delivered on FDA-cleared TMS devices over a standard 4–6 week schedule.",
    },
  ],
  relatedLinks: [
    {
      title: "What Is Treatment-Resistant Depression?",
      href: "/learn/what-is-treatment-resistant-depression",
      description: "Definition, causes, and treatment options",
    },
    {
      title: "SAINT® vs Deep TMS",
      href: "/learn/saint-vs-deep-tms",
      description: "SAINT® vs BrainsWay H-coil deep TMS compared",
    },
    {
      title: "SAINT® TMS Success Rates",
      href: "/learn/saint-tms-success-rates",
      description: "Remission and response data explained",
    },
    {
      title: "What Is Accelerated TMS?",
      href: "/what-is-accelerated-tms",
      description: "Complete guide to accelerated TMS protocols",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate-screener",
      description: "Free candidacy screener",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#quick-answer-saint-vs-mert-for-treatment-resistant-depression",
    },
    {
      label: "What MeRT Is & FDA Status",
      href: "#what-mert-is-and-its-fdaregulatory-status",
    },
    {
      label: "Evidence Side by Side",
      href: "#evidence-side-by-side-saint-vs-mert",
    },
    {
      label: "Time, Cost, Invasiveness",
      href: "#time-cost-and-invasiveness-saint-vs-mert",
    },
    { label: "Who Each Fits", href: "#who-each-protocol-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
